"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { useEstimatorSources } from "../_context/EstimatorSourcesContext";
import { forget, kept } from "../_logic/client-cache";
import { HOTEL_RATE_KEY_PREFIX, hotelRateKey } from "../_logic/hotel-rates";
import type { HotelRate } from "../_logic/types";
import { cityKey } from "../_logic/workspace";

/** A city's rate from an earlier visit, however old, else loading: shown while a fresh one is read. */
const knownRate = (city: string, code: string | null): HotelRate =>
  kept<HotelRate>(hotelRateKey(city, code))?.value ?? { status: "loading" };

/** Venue hotel rates for the destinations, and ways to read them again. */
export interface HotelRatesState {
  /** Rates keyed by {@link cityKey}: loading until that city's lookup answers. */
  rates: ReadonlyMap<string, HotelRate>;
  /** Reads every destination's rates again, e.g. with Refresh fares. */
  refresh: () => void;
}

/**
 * Keeps venue hotel rates loaded for every destination, keyed by {@link cityKey}. A city reads as
 * loading until its own lookup answers, so the table never shows "No rate data" early; a failed
 * lookup is `error` (retryable), not "no data". A city is read again when its pinned airport
 * changes, since the airport decides the country and state its venues are matched in. A rate this
 * browser read on an earlier visit shows at once, and is quietly replaced once it is stale.
 * @param destinations - Destination city names.
 * @param airports - Pinned airport codes keyed by {@link cityKey}.
 * @returns The rates and a refresh.
 * @throws Never.
 */
export function useHotelRates(destinations: string[], airports: Readonly<Record<string, string>>): HotelRatesState {
  // Hooks
  const sources = useEstimatorSources();

  // States
  const [rates, setRates] = useState<ReadonlyMap<string, HotelRate>>(
    () => new Map(destinations.map((city) => [cityKey(city), knownRate(city, airports[cityKey(city)] ?? null)]))
  );
  /** What each city was last requested with (`city|airport`), so unchanged cities aren't re-read. */
  const requested = useRef(new Map<string, string>());
  const [generation, setGeneration] = useState(0);

  // Handlers
  const refresh = useCallback(() => {
    requested.current.clear();
    forget(HOTEL_RATE_KEY_PREFIX);
    setGeneration((current) => current + 1);
  }, []);

  // Effects
  useEffect(() => {
    const pending = destinations.filter(
      (city) => requested.current.get(cityKey(city)) !== `${city}|${airports[cityKey(city)] ?? ""}`
    );
    if (pending.length === 0) return;
    pending.forEach((city) => requested.current.set(cityKey(city), `${city}|${airports[cityKey(city)] ?? ""}`));
    setRates(
      (prev) =>
        new Map([
          ...prev,
          ...pending.map((city) => [cityKey(city), knownRate(city, airports[cityKey(city)] ?? null)] as const),
        ])
    );

    // One request per city and airport for the page: a remount, a reopened estimate or an undo reuses it.
    for (const city of pending) {
      const code = airports[cityKey(city)] ?? null;
      void sources
        .hotelRate(city, code)
        .catch((): HotelRate => ({ status: "error" }))
        .then((rate) => {
          // A newer request for this city owns its result.
          if (requested.current.get(cityKey(city)) !== `${city}|${code ?? ""}`) return;
          setRates((prev) => new Map(prev).set(cityKey(city), rate));
        });
    }
  }, [destinations, airports, generation, sources]);

  return { rates, refresh };
}
